import type { ProfileCvContent, ProfileLanguage } from '../../redux/features/jobFormSlice';
import { isOngoingEnd, parseCvMonth } from './parse-cv-date';
import { languageCode } from './language-code';
import { loadSkillSynonyms, skillVariants } from './skill-synonyms';

/**
 * Quick CV-to-job match (TEC-711 phase 2). Compares the CV with the structured fields every
 * indexed job already carries, so it runs in the browser with no network or LLM call and the CV
 * never leaves the device for it. Deliberately literal: a skill counts as matched only when the
 * CV names it (or a translation of it, see skill-synonyms.ts), so the number is explainable ("7 of 11 skills") rather than a black-box score.
 * Free-text requirement sentences are out of scope here; judging those needs the full match view.
 */

export interface JobMatchJobInput {
  skillsAndTechnologies?: string[];
  requiredLanguages?: string[];
  optionalLanguages?: string[];
  minimumYearsOfExperience?: number;
}

export interface JobMatchCvInput {
  cv: ProfileCvContent;
  languages: ProfileLanguage[];
  licences: string[];
}

export interface JobMatchResult {
  /** Job skills the CV's skills list names. */
  matched: string[];
  /** Job skills the CV mentions in its text (a role description, the summary) but not in the skills list. */
  mentioned: string[];
  /** Job skills the CV doesn't mention at all. */
  missing: string[];
  requiredLanguages: Array<{ name: string; met: boolean }>;
  /** Total years of work history on the CV (overlaps merged), when the dates can be read. */
  cvYears: number | null;
  requiredYears: number | null;
  /** matched + mentioned over all job skills, 0..100; null when the job lists no skills. */
  skillsPercent: number | null;
  /** True when there is enough on the CV to compare at all. */
  hasCv: boolean;
}

const STOPWORDS = new Set(['and', 'ja', 'och', 'of', 'the', 'in', 'for', 'tai', 'eller', 'or', '&']);

/** Lowercase, drop ".js"-style suffixes and punctuation that doesn't carry meaning (keeps c++, c#, .net). */
export function normaliseSkill(s: string): string {
  return s
    .toLowerCase()
    .replace(/\.js\b/g, '')
    .replace(/[()[\]{}"'“”,;:!?]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function words(s: string): string[] {
  return normaliseSkill(s)
    .split(/[\s/–-]+/)
    .filter((w) => w && !STOPWORDS.has(w));
}

function literalSkillsMatch(na: string, nb: string): boolean {
  if (!na || !nb) return false;
  if (na === nb) return true;
  const wa = words(na);
  const wb = words(nb);
  const [short, long] = wa.length <= wb.length ? [wa, wb] : [wb, wa];
  if (short.length === 0 || short.every((w) => w.length < 2)) return false;
  return short.every((w) => long.includes(w));
}

/**
 * Two skill names match when they normalise equal, or when every word of the shorter one appears
 * in the longer one ("Excel" vs "Microsoft Excel", "SAP" vs "SAP S/4HANA"). Single-letter words
 * never carry a match on their own. Once the synonym data has loaded (loadSkillSynonyms), a
 * translation counts too: "Asiakaspalvelu" matches "Customer service skills".
 */
export function skillsMatch(a: string, b: string): boolean {
  const na = normaliseSkill(a);
  const nb = normaliseSkill(b);
  if (literalSkillsMatch(na, nb)) return true;
  if (!na || !nb) return false;
  const vb = skillVariants(nb);
  return skillVariants(na).some((x) => vb.some((y) => (x !== na || y !== nb) && literalSkillsMatch(x, y)));
}

/** Starts loading the cross-language synonym data; matching uses it once it arrives. */
export function loadSkillSynonymData(): Promise<void> {
  return loadSkillSynonyms(normaliseSkill);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * True when the skill, or one of its translations, appears as its own word(s) in the text. Names
 * of six letters or more may carry up to four more letters, so Finnish case endings still count
 * ("asiakaspalvelussa", "projektinhallintaa").
 */
export function textMentionsSkill(text: string, skill: string): boolean {
  const n = normaliseSkill(skill);
  if (n.length < 2) return false;
  const haystack = text.toLowerCase();
  return skillVariants(n).some((v) => {
    if (v.length < 2) return false;
    const ending = v.length >= 6 && /\p{L}$/u.test(v) ? '\\p{L}{0,4}' : '';
    const pattern = new RegExp(`(^|[^\\p{L}\\p{N}+#])${escapeRegExp(v)}${ending}(?=$|[^\\p{L}\\p{N}+#])`, 'iu');
    return pattern.test(haystack);
  });
}

/** Years of work history with overlapping roles merged; null when no role has readable dates. */
export function cvWorkYears(cv: ProfileCvContent, now: Date = new Date()): number | null {
  const nowIndex = now.getFullYear() * 12 + now.getMonth();
  const spans = cv.profileWorkExperiences
    .map((e) => {
      const start = parseCvMonth(e.startDate, 'start');
      if (start === null) return null;
      const end = isOngoingEnd(e.endDate) ? nowIndex : parseCvMonth(e.endDate, 'end');
      return end === null || end < start ? null : { start, end };
    })
    .filter((s): s is { start: number; end: number } => s !== null)
    .sort((a, b) => a.start - b.start);
  if (spans.length === 0) return null;
  let months = 0;
  let curStart = spans[0].start;
  let curEnd = spans[0].end;
  for (const s of spans.slice(1)) {
    if (s.start <= curEnd + 1) {
      curEnd = Math.max(curEnd, s.end);
    } else {
      months += curEnd - curStart + 1;
      curStart = s.start;
      curEnd = s.end;
    }
  }
  months += curEnd - curStart + 1;
  return Math.floor(months / 12);
}

export function hasCvContent(cv: ProfileCvContent): boolean {
  return Boolean(
    cv.profileTitle.trim() ||
      cv.profileWorkExperiences.length ||
      cv.profileEducation.length ||
      cv.profileSkills.some((s) => s.trim())
  );
}

export function matchCvToJob({ cv, languages, licences }: JobMatchCvInput, job: JobMatchJobInput): JobMatchResult {
  const jobSkills = Array.from(new Set((job.skillsAndTechnologies ?? []).map((s) => s.trim()).filter(Boolean)));
  const cvSkills = [...cv.profileSkills, ...licences].map((s) => s.trim()).filter(Boolean);
  const cvText = [
    cv.profileTitle,
    cv.profileSummary,
    ...cv.profileWorkExperiences.flatMap((e) => [e.title, e.description ?? '']),
    ...cv.profileEducation.map((e) => e.degree),
    ...cv.profileProjects.flatMap((p) => [p.title, p.description]),
  ].join('\n');

  const matched: string[] = [];
  const mentioned: string[] = [];
  const missing: string[] = [];
  for (const skill of jobSkills) {
    if (cvSkills.some((s) => skillsMatch(s, skill))) matched.push(skill);
    else if (textMentionsSkill(cvText, skill)) mentioned.push(skill);
    else missing.push(skill);
  }

  const cvLangCodes = new Set(languages.map((l) => languageCode(l.language)));
  const requiredLanguages = (job.requiredLanguages ?? [])
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name, met: cvLangCodes.has(languageCode(name)) }));

  return {
    matched,
    mentioned,
    missing,
    requiredLanguages,
    cvYears: cvWorkYears(cv),
    requiredYears: typeof job.minimumYearsOfExperience === 'number' && job.minimumYearsOfExperience > 0 ? job.minimumYearsOfExperience : null,
    skillsPercent: jobSkills.length ? Math.round(((matched.length + mentioned.length) / jobSkills.length) * 100) : null,
    hasCv: hasCvContent(cv),
  };
}

/**
 * The CV bucket to compare with a job: the one in the job page's language when it has content,
 * otherwise the fullest bucket, so an English-only CV still matches a Finnish job page.
 */
export function pickCvForLocale<L extends string>(byLocale: Record<L, ProfileCvContent>, locale: string): { locale: L; cv: ProfileCvContent } | null {
  const entries = Object.entries(byLocale) as Array<[L, ProfileCvContent]>;
  const preferred = entries.find(([l]) => l === locale);
  if (preferred && hasCvContent(preferred[1])) return { locale: preferred[0], cv: preferred[1] };
  const filled = entries.filter(([, c]) => hasCvContent(c));
  if (filled.length === 0) return null;
  const size = (c: ProfileCvContent) => c.profileWorkExperiences.length + c.profileEducation.length + c.profileSkills.length + (c.profileTitle ? 1 : 0);
  const [best] = filled.sort((a, b) => size(b[1]) - size(a[1]));
  return { locale: best[0], cv: best[1] };
}
